import React, { ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Portal from '../../../../components/Portal';
import { useTranslation } from 'react-i18next';

export interface IOSActionSheetItem {
  id: string;
  label: string;
  icon?: ReactNode;
  description?: string;
  destructive?: boolean;
  isSelected?: boolean;
  onClick: () => void;
}

interface IOSActionSheetProps {
  isOpen: boolean;
  onClose: () => void;
  items: IOSActionSheetItem[];
  title?: string;
  message?: string;
}

/**
 * iOS风格的操作表单组件
 * 参考iOS系统原生ActionSheet设计
 */
export const IOSActionSheet: React.FC<IOSActionSheetProps> = ({
  isOpen,
  onClose,
  items,
  title,
  message
}) => {
  const { t } = useTranslation(['v3/common']);

  // 处理背景点击
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Portal>
          <div
            className="fixed inset-0 z-[999] flex items-end justify-center bg-black/40 dark:bg-black/60 backdrop-blur-lg"
            onClick={handleBackdropClick}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
          >
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="w-full max-w-sm mx-auto mb-2 px-2"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Actions Group (including title/message if present) */}
            <div className="bg-ios-modal-bg-light/90 dark:bg-ios-modal-bg-dark/90 backdrop-blur-xl rounded-xl overflow-hidden shadow-md">
              {/* Title and Message (if provided) */}
              {(title || message) && (
                <div className="px-4 pt-4 pb-2 text-center border-b border-ios-separator-light dark:border-ios-separator-dark">
                  {title && (
                    <h3 className="text-ios-subhead font-medium text-ios-secondary-label-light dark:text-ios-secondary-label-dark mb-0.5">
                      {title}
                    </h3>
                  )}
                  {message && (
                    <p className="text-ios-footnote text-ios-secondary-label-light dark:text-ios-secondary-label-dark">
                      {message}
                    </p>
                  )}
                </div>
              )}
              {/* Action Buttons */}
              {items.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => {
                    item.onClick();
                    onClose();
                  }}
                  className={`w-full px-4 py-3.5 text-lg flex items-center transition-colors duration-150 ease-in-out
                    ${index !== 0 ? 'border-t border-ios-separator-light dark:border-ios-separator-dark' : ''}
                    ${item.destructive
                      ? 'text-ios-red dark:text-ios-dark-red active:bg-ios-red/10 dark:active:bg-ios-dark-red/10'
                      : item.isSelected
                        ? 'text-ios-blue dark:text-ios-dark-blue active:bg-ios-blue/10 dark:active:bg-ios-dark-blue/10'
                        : 'text-ios-black dark:text-white active:bg-ios-gray5/50 dark:active:bg-ios-dark-gray4/50'
                    }
                  `}
                >
                  {item.icon && <span className="mr-3 -ml-1 flex-shrink-0 w-5 h-5 flex items-center justify-center">{item.icon}</span>}
                  <div className="flex flex-col items-start flex-grow">
                    <span className="text-left">
                      {item.label}
                    </span>
                    {item.description && (
                      <span className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray mt-0.5 text-left">
                        {item.description}
                      </span>
                    )}
                  </div>
                  {item.isSelected && (
                    <svg className="w-5 h-5 ml-2 text-ios-blue dark:text-ios-dark-blue flex-shrink-0" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M16.6668 5L7.50016 14.1667L3.3335 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              ))}
            </div>

            {/* Cancel Button */}
            <div className="bg-ios-modal-bg-light/95 dark:bg-ios-modal-bg-dark/95 backdrop-blur-xl rounded-xl mt-2 overflow-hidden shadow-md">
              <button
                onClick={onClose}
                className="w-full px-4 py-3.5 text-center text-lg font-semibold text-ios-blue dark:text-ios-dark-blue active:bg-ios-blue/10 dark:active:bg-ios-dark-blue/10 transition-colors duration-150 ease-in-out"
              >
                {t('v3/common:actions.cancel')}
              </button>
            </div>

            {/* Bottom Safe Area Padding */}
            <div className="h-[env(safe-area-inset-bottom)]"></div>
          </motion.div>
          </div>
        </Portal>
      )}
    </AnimatePresence>
  );
};
